import { useState, useEffect, useRef, useCallback } from 'react';
import { TIMING } from './constants';

interface UseAutoRotateOptions {
  totalItems: number;
  intervalMs?: number;
  onIndexChange?: (index: number) => void;
}

interface UseAutoRotateReturn {
  currentIndex: number;
  isPaused: boolean;
  progress: number; // 0-100 for progress bar fill
  pause: () => void;
  resume: () => void;
  goTo: (index: number) => void;
  next: () => void;
  previous: () => void;
}

export const useAutoRotate = ({
  totalItems,
  intervalMs = TIMING.autoRotateInterval,
  onIndexChange,
}: UseAutoRotateOptions): UseAutoRotateReturn => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(0);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(Date.now());

  // Clear all intervals
  const clearIntervals = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }, []);

  // Start progress tracking
  const startProgress = useCallback(() => {
    // Clear existing progress interval before starting a new one
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }

    startTimeRef.current = Date.now();
    setProgress(0);

    progressIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      const newProgress = Math.min((elapsed / intervalMs) * 100, 100);
      setProgress(newProgress);
    }, TIMING.progressUpdateInterval);
  }, [intervalMs]);

  // Advance to next item
  const advanceToNext = useCallback(() => {
    setCurrentIndex((prev) => {
      const next = (prev + 1) % totalItems;
      onIndexChange?.(next);
      return next;
    });
    startProgress();
  }, [totalItems, onIndexChange, startProgress]);

  // Start auto-rotation
  const startRotation = useCallback(() => {
    clearIntervals();
    startProgress();

    intervalRef.current = setInterval(() => {
      advanceToNext();
    }, intervalMs);
  }, [clearIntervals, startProgress, advanceToNext, intervalMs]);

  // Pause auto-rotation
  const pause = useCallback(() => {
    setIsPaused(true);
    clearIntervals();
  }, [clearIntervals]);

  // Resume auto-rotation
  const resume = useCallback(() => {
    setIsPaused(false);
    startRotation();
  }, [startRotation]);

  // Go to specific index
  const goTo = useCallback(
    (index: number) => {
      if (index >= 0 && index < totalItems) {
        setCurrentIndex(index);
        onIndexChange?.(index);
        // Reset timer when manually navigating
        if (!isPaused) {
          startRotation();
        }
      }
    },
    [totalItems, onIndexChange, isPaused, startRotation],
  );

  // Go to next item
  const next = useCallback(() => {
    goTo((currentIndex + 1) % totalItems);
  }, [currentIndex, totalItems, goTo]);

  // Go to previous item
  const previous = useCallback(() => {
    goTo((currentIndex - 1 + totalItems) % totalItems);
  }, [currentIndex, totalItems, goTo]);

  // Initialize auto-rotation on mount
  useEffect(() => {
    // Check for reduced motion preference
    if (typeof window !== 'undefined') {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (prefersReducedMotion) {
        // Don't auto-rotate for users who prefer reduced motion
        return;
      }
    }

    startRotation();

    return () => {
      clearIntervals();
    };
  }, [startRotation, clearIntervals]);

  return {
    currentIndex,
    isPaused,
    progress,
    pause,
    resume,
    goTo,
    next,
    previous,
  };
};
